import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { 
  UtensilsCrossed, Sparkles, Pill, ShoppingBag, Gift, Package,
  Gauge, Clock, Palette, Award, Scissors, MapPin,
  CheckCircle2, ArrowRight, Phone, Mail, MapPinIcon, ExternalLink
} from 'lucide-react';
import { 
  heroData, statsData, introData, productsData, cartonCapabilitiesData,
  dieCuttingData, industriesData, whyChooseData, faqData,
  ctaData, finalCtaData, contactData, contactFormData
} from '../data/mock';

const iconMap = {
  UtensilsCrossed, Sparkles, Pill, ShoppingBag, Gift, Package,
  Gauge, Clock, Palette, Award, Scissors, MapPin
};

export const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
        
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 5000);
      } else {
        throw new Error(data.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please try emailing us directly at sales@businessprint.ie');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <div className="logo-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_pack-sleeve-hub/artifacts/m19yvs3v_Screenshot%202026-03-23%20at%2014.55.13.png"
              alt="Business Print & Packaging Logo"
              className="company-logo"
              width="180"
              height="60"
            />
          </div>
          <nav className="main-nav">
            <a href="#services" className="nav-link">Services</a>
            <a href="#capabilities" className="nav-link">Capabilities</a>
            <a href="#industries" className="nav-link">Industries</a>
            <a href="#faq" className="nav-link">FAQ</a>
            <a href="#contact" className="nav-link">Contact</a>
          </nav>
          <Button onClick={scrollToContact} className="header-cta">
            Request a Quote
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section-v2">
        <div className="hero-container-v2">
          <div className={`hero-content-v2 ${isVisible ? 'fade-in' : ''}`}>
            <h1 className="hero-title-v2">
              {heroData.title}
            </h1>
            <p className="hero-text-v2">
              {heroData.subtitle}
            </p>
            <p className="hero-description-v2">
              {heroData.description}
            </p>
            <div className="hero-cta-group">
              <Button size="lg" className="cta-primary" asChild>
                <a href={`mailto:${contactData.email}?subject=Packaging%20Quote%20Request`}>
                  {heroData.ctaText}
                  <ArrowRight className="ml-2" size={20} />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="cta-secondary" onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>
                {heroData.ctaSecondary}
              </Button>
            </div>
          </div>
          <div className={`hero-image-container-v2 ${isVisible ? 'slide-in' : ''}`}>
            <img 
              src={heroData.heroImage} 
              alt="Heidelberg Speedmaster CX 75 printing press at Business Print Dublin facility producing high-quality folded cartons and printed packaging" 
              className="hero-image-v2"
              width="600"
              height="450"
            />
          </div>
        </div>
      </section>

      {/* Stats Badges */}
      <section className="stats-badges-section">
        <div className="stats-badges-container">
          {statsData.map((stat, index) => (
            <div key={index} className="stat-badge">
              <CheckCircle2 size={14} className="stat-badge-icon" />
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="about-container">
          <div className="about-content">
            {introData.badge && <div className="section-badge">{introData.badge}</div>}
            <h2 className="about-title">{introData.title}</h2>
            <div className="about-text">
              {introData.content.split('\n\n').map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          </div>
          <div className="about-image-wrapper">
            <img 
              src={introData.image} 
              alt="Heidelberg CX75 lithographic printing press producing custom printed packaging and folded cartons at Business Print Dublin"
              className="about-image"
              loading="lazy"
              width="500"
              height="400"
            />
            <div className="about-image-badge">
              <div className="badge-title">{introData.badge2}</div>
              <div className="badge-subtitle">{introData.badgeText}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="section-container">
          <div className="section-badge-center">Our Services</div>
          <div className="section-header">
            <h2 className="section-title">Packaging Products & Services</h2>
            <p className="section-subtitle">
              From printed sleeves to custom cartons, we offer a complete range of packaging solutions for Irish businesses.
            </p>
          </div>
          <div className="services-grid">
            {productsData.map((product) => (
              <Card key={product.id} className="service-card">
                <div className="service-image-wrapper">
                  <img 
                    src={product.image} 
                    alt={`${product.title} - custom printed packaging solutions for Irish businesses by Business Print Dublin`}
                    className="service-image"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                </div>
                <CardContent className="service-content">
                  <h3 className="service-title">{product.title}</h3>
                  <p className="service-description">{product.description}</p>
                  {product.link && product.link !== '#' && (
                    <a href={product.link} className="service-link" target="_blank" rel="noopener noreferrer">
                      Learn More <ExternalLink size={14} />
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Carton Gluing Capabilities Section */}
      <section id="capabilities" className="capabilities-section-v2">
        <div className="section-container">
          <div className="section-badge-center">Finishing Capabilities</div>
          <div className="section-header">
            <h2 className="section-title">Carton Gluing & Packaging Finishing</h2>
            <p className="section-subtitle">
              Our in-house carton gluing capabilities cover all major carton styles, ensuring your packaging is production-ready and professionally finished.
            </p>
            <a href="https://www.businessprint.ie/what-we-do/folded-cartons-and-boxes.html" className="section-link" target="_blank" rel="noopener noreferrer">
              Learn more about our folded cartons <ExternalLink size={16} />
            </a>
          </div>
          <div className="capabilities-grid-v2">
            {cartonCapabilitiesData.map((capability) => (
              <div key={capability.id} className="capability-card-v2">
                <div className="capability-image-container">
                  <img 
                    src={capability.image} 
                    alt={`${capability.title} technical diagram showing carton construction with dimension labels for precision manufacturing`}
                    className="capability-diagram"
                    loading="lazy"
                    width="350"
                    height="250"
                  />
                </div>
                <div className="capability-info">
                  <h3 className="capability-title-v2">{capability.title}</h3>
                  <p className="capability-description-v2">{capability.description}</p>
                  <ul className="capability-features-list">
                    {capability.features.map((feature, idx) => (
                      <li key={idx} className="capability-feature">
                        <CheckCircle2 size={16} className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Die Cutting Section */}
      <section className="die-cutting-section-v2">
        <div className="die-cutting-container">
          <div className="die-cutting-image-side">
            <img 
              src={dieCuttingData.image} 
              alt="BOBST SP 102 precision die cutting machine for custom packaging shapes and folded carton production at Business Print Dublin"
              className="die-cutting-machine-image"
              loading="lazy"
              width="550"
              height="400"
            />
          </div>
          <div className="die-cutting-content-side">
            <div className="section-badge">{dieCuttingData.badge}</div>
            <h2 className="die-cutting-title-v2">{dieCuttingData.title}</h2>
            <p className="die-cutting-text">{dieCuttingData.subtitle}</p>
            <ul className="die-cutting-features-v2">
              {dieCuttingData.features.map((feature, index) => (
                <li key={index} className="die-cutting-feature">
                  <CheckCircle2 size={18} className="feature-check-green" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <p className="die-cutting-footer-text">{dieCuttingData.description}</p>
            <a href={dieCuttingData.link} className="die-cutting-link" target="_blank" rel="noopener noreferrer">
              {dieCuttingData.linkText} <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="industries-section-v2">
        <div className="section-container">
          <div className="section-badge-center">Sectors We Serve</div>
          <div className="section-header">
            <h2 className="section-title">Industries We Serve</h2>
            <p className="section-subtitle">
              We supply printed packaging to businesses across multiple sectors, understanding the unique requirements of each industry.
            </p>
          </div>
          <div className="industries-grid-v2">
            {industriesData.map((industry) => {
              const IconComponent = iconMap[industry.icon];
              return (
                <div key={industry.id} className="industry-card-v2">
                  <div className="industry-icon-box">
                    <IconComponent size={28} className="industry-icon-v2" />
                  </div>
                  <h3 className="industry-title-v2">{industry.title}</h3>
                  <p className="industry-description-v2">{industry.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="why-choose-section-v2">
        <div className="section-container">
          <div className="section-badge-center">{whyChooseData.badge}</div>
          <div className="section-header">
            <h2 className="section-title">{whyChooseData.title}</h2>
            <p className="section-subtitle">{whyChooseData.subtitle}</p>
          </div>
          <div className="why-choose-grid-v2">
            {whyChooseData.reasons.map((reason, index) => {
              const IconComponent = iconMap[reason.icon];
              return (
                <div key={index} className="why-choose-card">
                  <div className="why-choose-icon-circle">
                    <IconComponent size={24} className="why-choose-icon-v2" />
                  </div>
                  <h3 className="why-choose-title-v2">{reason.title}</h3>
                  <p className="why-choose-description-v2">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="faq-container">
          <div className="section-badge-center">{faqData.badge}</div>
          <div className="section-header">
            <h2 className="section-title">{faqData.title}</h2>
            <p className="section-subtitle">{faqData.subtitle}</p>
          </div>
          <Accordion type="single" collapsible className="faq-accordion">
            {faqData.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="faq-item">
                <AccordionTrigger className="faq-question">{faq.question}</AccordionTrigger>
                <AccordionContent className="faq-answer">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section-v2">
        <div className="contact-container-v2">
          <div className="contact-left">
            <div className="section-badge">{ctaData.badge}</div>
            <h2 className="contact-title-v2">{ctaData.title}</h2>
            <p className="contact-subtitle-v2">{ctaData.subtitle}</p>
            
            <div className="contact-info-grid">
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <MapPinIcon size={20} />
                </div>
                <div>
                  <div className="contact-info-label">Location</div>
                  <div className="contact-info-value">{contactData.address}</div>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Phone size={20} />
                </div>
                <div>
                  <div className="contact-info-label">Phone</div>
                  <a href={`tel:${contactData.phoneLink}`} className="contact-info-value contact-link">
                    {contactData.phone}
                  </a>
                </div>
              </div>
              <div className="contact-info-item">
                <div className="contact-info-icon">
                  <Mail size={20} />
                </div>
                <div>
                  <div className="contact-info-label">Email</div>
                  <a href={`mailto:${contactData.email}`} className="contact-info-value contact-link">
                    {contactData.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="contact-team-image">
              <img 
                src={contactData.image} 
                alt="Business Print packaging team in Dublin facility ready to assist with custom printed packaging and folded carton solutions"
                className="team-image"
                loading="lazy"
                width="500"
                height="400"
              />
            </div>
          </div>

          <div className="contact-right">
            <Card className="contact-form-card-v2">
              <CardContent className="contact-form-content-v2">
                <h3 className="contact-form-title">{contactFormData.title}</h3>
                {submitSuccess ? (
                  <div className="success-message">
                    <CheckCircle2 size={48} className="success-icon" />
                    <h3>Quote Request Received!</h3>
                    <p>Thank you for your enquiry. Our packaging team will be in touch shortly.</p>
                  </div>
                ) : (
                  <>
                    <form onSubmit={handleSubmit} className="contact-form">
                      {contactFormData.fields.map((field) => (
                        <div key={field.name} className="form-group">
                          <label htmlFor={field.name} className="form-label">
                            {field.label} {field.required && '*'}
                          </label>
                          {field.type === 'textarea' ? (
                            <Textarea
                              id={field.name}
                              name={field.name}
                              placeholder={field.placeholder}
                              value={formData[field.name]}
                              onChange={handleInputChange}
                              required={field.required}
                              rows={5}
                              className="form-textarea"
                            />
                          ) : (
                            <Input
                              id={field.name}
                              name={field.name}
                              type={field.type}
                              placeholder={field.placeholder}
                              value={formData[field.name]}
                              onChange={handleInputChange}
                              required={field.required}
                              className="form-input"
                            />
                          )}
                        </div>
                      ))}
                      <Button 
                        type="submit" 
                        className="form-submit-btn"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Sending...' : contactFormData.submitText}
                      </Button>
                    </form>
                    <div className="direct-email-option">
                      <p className="direct-email-text">
                        Prefer email? Contact us directly at{' '}
                        <a href="mailto:sales@businessprint.ie" className="direct-email-link">
                          sales@businessprint.ie
                        </a>
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="final-cta-container">
          <h2 className="final-cta-title">{finalCtaData.title}</h2>
          <p className="final-cta-description">{finalCtaData.description}</p>
          <div className="final-cta-buttons">
            <Button size="lg" className="cta-primary" asChild>
              <a href={`mailto:${contactData.email}?subject=Packaging%20Quote%20Request`}>
                {finalCtaData.ctaText}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="cta-secondary" onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>
              {finalCtaData.ctaSecondary}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-content-v2">
            <div className="footer-section">
              <h4 className="footer-heading">{contactData.company}</h4>
              <p className="footer-text">
                Commercial printing and packaging solutions in Dublin, Ireland. Specialising in printed cartons, folded packaging and custom packaging boxes.
              </p>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Services</h4>
              <nav className="footer-nav">
                <a href="#services" className="footer-link">Printed Packaging</a>
                <a href="#capabilities" className="footer-link">Folded Cartons</a>
                <a href="#capabilities" className="footer-link">Carton Gluing</a>
                <a href="#capabilities" className="footer-link">Die Cutting</a>
              </nav>
            </div>
            <div className="footer-section">
              <h4 className="footer-heading">Contact</h4>
              <div className="footer-contact">
                <p>{contactData.phone}</p>
                <p>{contactData.email}</p>
                <p>{contactData.address}</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 {contactData.company}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
